import cx from 'classnames';
import * as React from 'react';

import { ReactionContainer } from 'chat/components/Reaction';
import Avatar from 'components/Avatar';
import CurrentUser from 'auth/CurrentUser';
import ErrorReporter from 'app/components/ErrorReporter';
import GroupAvatar, { FullExpectedGroup } from 'groups/components/GroupAvatar';
import ReactionDisplay from 'chat/components/ReactionDisplay';
import ReactionStatus from 'chat/components/ReactionStatus';
import Time from 'components/Time';
import UserAvatar, { UnsafePartialUser } from 'users/components/UserAvatar';
import UrgentAvatar from 'chat/components/UrgentAvatar';

import AnnouncementMessageItemHeader from './AnnouncementMessageItemHeader';
import AnnouncementMessageItemHeaderDeprecated from './AnnouncementMessageItemHeader/AnnouncementMessageItemHeaderDeprecated';
import MessageBodyTranslationSwitcher from '../_shared/MessageBodyTranslationSwitcher';
import MessageItemBody from '../../../../_shared/MessageItemBody';

import './AnnouncementMessageItem.scss';

/* 
this is a hack because class_name is not present on FullExpectedGroup
there is probably a better way to extend FullExpectedGroup and add it there
*/
type Group = FullExpectedGroup | any;
/* 
this is a hack because uuid is not present on UnsafePartialUser
there is probably a better way to extend UnsafePartialUser and add it there
*/
type Sender = UnsafePartialUser | any;

export type Props = {
  item: {
    item: {
      files: any;
      reactionSummary: any;
      linkPreviews: any;
      sender: Sender;
      urgent: boolean;
      group: Group;
      locale: string;
      body: string;
      canReact: boolean;
      sentAt: string;
      createdAt: string;
      uuid: string;
      target: {
        description: string;
        title: string;
        iconUrls: [string];
      };
      translationDetails: {
        originalText: string;
        originalLanguageCode: string;
      };
    };
  };
  streamUuid: string;
  isLastItemOfChunk: boolean;
  canViewDeliveryInformation: boolean;
  isAuthor?: boolean;
  renderScheduledAnnounementButtons?: () => React.ReactElement;
};

type State = {
  reactionPickerVisible: boolean;
  hovered: boolean;
  showOriginal: boolean;
};

class AnnouncementMessageItem extends React.Component<Props, State> {
  state: State = {
    reactionPickerVisible: false,
    hovered: false,
    showOriginal: false,
  };

  handleEnterHover = () => this.setState({ hovered: true });
  handleLeaveHover = () => this.setState({ hovered: false });

  isAuthor() {
    return this.props.isAuthor || CurrentUser.getUserUuid() === this.props.item.item.sender.uuid;
  }

  renderDroplet() {
    const { isLastItemOfChunk } = this.props;

    if (!isLastItemOfChunk || this.isAuthor()) {
      return null;
    }

    return (
      <span className="AnnouncementMessageItem-droplet">
        <UserAvatar user={this.props.item.item.sender} size="small" />
      </span>
    );
  }

  renderAnnouncementHeader() {
    const { streamUuid, canViewDeliveryInformation } = this.props;
    const { group, sender, target, uuid, reactionSummary, urgent } = this.props.item.item;
    const headerComponent = (
      avatar: any,
      targetTitle: string,
      targetDescription?: string | null
    ) => {
      const props = {
        targetTitle,
        targetDescription,
        canViewDeliveryInformation,
        streamUuid,
        messageUuid: uuid,
        reactions: reactionSummary ? reactionSummary.reactions : null,
      };
      let Component:
        | typeof AnnouncementMessageItemHeader
        | typeof AnnouncementMessageItemHeaderDeprecated = AnnouncementMessageItemHeader;
      if (!CurrentUser.getUser()?.hasFeature('hide_messages.web.oct-22'))
        Component = AnnouncementMessageItemHeaderDeprecated;
      if (urgent) {
        return (
          <Component
            {...props}
            avatar={
              <UrgentAvatar size="small" className="AnnouncementMessageItem-announcementBadge" />
            }
            urgent
          />
        );
      }
      return <Component {...props} avatar={avatar} />;
    };

    if (target) {
      const avatar = (
        <Avatar image={target.iconUrls[0]} className="AnnouncementMessageItem-announcementBadge" />
      );
      return headerComponent(avatar, target.title, target.description);
    }

    if (group) {
      const avatar = (
        <GroupAvatar className="AnnouncementMessageItem-announcementBadge" group={group} />
      );
      const targetText = group.class_name;
      return headerComponent(avatar, targetText);
    }

    if (sender && sender.__typename === 'EntitySender') {
      const avatar = (
        <GroupAvatar
          className="AnnouncementMessageItem-announcementBadge"
          group={{ groupAvatar: { svg_url: sender.avatarUrl } }}
        />
      );
      const targetText = sender.name;
      return headerComponent(avatar, targetText);
    }

    return (
      <ErrorReporter error="AnnouncementMessageItem - tried to render item header without target, group, or sender" />
    );
  }

  renderReactionStatus() {
    const messageUuid = this.props.item.item.uuid;
    const { streamUuid } = this.props;

    if (!streamUuid) {
      return (
        <ErrorReporter error="AnnouncementMessageItem - tried to render reaction status without streamUuid" />
      );
    } else if (!messageUuid) {
      return (
        <ErrorReporter error="AnnouncementMessageItem - tried to render reaction status without messageUuid" />
      );
    }

    return (
      <ReactionContainer chatUuid={streamUuid} messageUuid={messageUuid}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => (
          <ReactionStatus
            sentBySelf={this.isAuthor()}
            reactionSummary={reactionSummary}
            addReactionToSummary={addReactionToSummary}
            removeReactionFromSummary={removeReactionFromSummary}
          />
        )}
      </ReactionContainer>
    );
  }

  renderReactions() {
    const messageUuid = this.props.item.item.uuid;
    const { streamUuid } = this.props;

    if (!streamUuid) {
      return (
        <ErrorReporter error="AnnouncementMessageItem - tried to render reactions without streamUuid" />
      );
    } else if (!messageUuid) {
      return (
        <ErrorReporter error="AnnouncementMessageItem - tried to render reactions without messageUuid" />
      );
    }

    return (
      <ReactionContainer chatUuid={streamUuid} messageUuid={messageUuid}>
        {({ reactionSummary, addReactionToSummary, removeReactionFromSummary }) => (
          <ReactionDisplay
            sentBySelf={this.isAuthor()}
            reactionSummary={reactionSummary}
            addReactionToSummary={addReactionToSummary}
            removeReactionFromSummary={removeReactionFromSummary}
          />
        )}
      </ReactionContainer>
    );
  }

  hasTranslations() {
    return !this.isAuthor() && this.props.item.item.translationDetails;
  }

  originalLanguageCode() {
    const { translationDetails } = this.props.item.item;
    if (!translationDetails) {
      return '';
    }

    return translationDetails.originalLanguageCode;
  }

  toggleOriginal = () => {
    this.setState({ showOriginal: !this.state.showOriginal });
  };

  renderTranslationSwitcher() {
    const { showOriginal } = this.state;

    return (
      <MessageBodyTranslationSwitcher
        showOriginal={showOriginal}
        originalLanguageCode={this.originalLanguageCode()}
        onToggleShowOriginal={this.toggleOriginal}
      />
    );
  }

  renderHover() {
    const { item } = this.props.item;
    const date = item.sentAt || item.createdAt;

    return date ? (
      <div className="AnnouncementMessageItem-hover">
        <div className="MessageItem-hover-receipt">
          <Time date={new Date(date)} hour="numeric" minute="2-digit" />
        </div>
      </div>
    ) : null;
  }

  render() {
    const { item } = this.props.item;
    const { hovered, reactionPickerVisible } = this.state;

    return (
      <div
        className={cx('AnnouncementMessageItem', {
          'AnnouncementMessageItem-self': this.isAuthor(),
          'AnnouncementMessageItem-other': !this.isAuthor(),
          'AnnouncementMessageItem-hovered': hovered || reactionPickerVisible,
        })}
      >
        <div
          className="AnnouncementMessageItem-row"
          onMouseEnter={this.handleEnterHover}
          onMouseLeave={this.handleLeaveHover}
        >
          {this.renderHover()}
          <div className="AnnouncementMessageItem-column">
            <div className="AnnouncementMessageItem-dropletChubble">
              <div className="AnnouncementMessageItem-chubble">
                {this.renderAnnouncementHeader()}
                <div className="AnnouncementMessageItem-content">
                  <MessageItemBody item={item} showOriginal={this.state.showOriginal} />
                </div>
              </div>
              {this.renderDroplet()}
            </div>
            {this.hasTranslations() && this.renderTranslationSwitcher()}
            {item.canReact && this.renderReactionStatus()}
            {this.props.renderScheduledAnnounementButtons &&
              this.props.renderScheduledAnnounementButtons()}
          </div>
        </div>
        {item.canReact && this.renderReactions()}
      </div>
    );
  }
}

export default AnnouncementMessageItem;
